import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MoonStar, Globe } from "lucide-react";
import { toast } from "sonner";

const DEMO_ACCOUNTS = [
  { email: "midzham.glocal@gmail.com", label: "Owner / Super Admin", role: "super_admin" },
  { email: "aisyah.staff@travelos.my", label: "Staff — Aisyah", role: "staff" },
  { email: "razak.agent@travelos.my", label: "Agent — Razak", role: "agent" },
  { email: "customer.demo@travelos.my", label: "Customer / Jemaah", role: "customer" },
];

export default function Login() {
  const { loginGoogle, loginDemo } = useAuth();
  const { lang, toggle } = useLang();
  const navigate = useNavigate();

  const demoLogin = async (email) => {
    try {
      await loginDemo(email);
      navigate("/app");
    } catch {
      toast.error("Demo login failed");
    }
  };

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center p-6">
      <div className="absolute top-6 right-6">
        <button data-testid="login-language-toggle" onClick={toggle}
          className="flex items-center gap-2 text-slate-500 hover:text-emerald-700 text-xs font-mono uppercase tracking-widest transition-colors">
          <Globe className="w-4 h-4" /> {lang === "en" ? "BM" : "EN"}
        </button>
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-10 animate-fade-up">
          <img src="/logo.jpeg" alt="Travel Hub Desk" className="w-20 h-20 rounded-2xl object-cover shadow-md mb-6 mx-auto" />
          <h1 className="font-serif text-3xl text-slate-900 tracking-tight">Travel Hub Desk</h1>
          <p className="text-slate-500 text-xs mt-2 font-mono uppercase tracking-[0.15em]">Autonomous Travel Agent Booking Business Technology</p>
          <p className="text-slate-400 text-[10px] mt-1.5 font-mono uppercase tracking-[0.1em]">CRM • Package • Booking • Inventory • Agents • Operations • Payments</p>
        </div>
        <div className="bg-white border border-sand-border rounded-2xl p-8 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.12)] animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <Button data-testid="google-login-button" onClick={loginGoogle}
            className="w-full bg-white text-slate-800 hover:bg-slate-50 border border-slate-300 font-semibold h-12 rounded-xl shadow-sm">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a7.2 7.2 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52Z"/></svg>
            Continue with Google
          </Button>
          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-sand-border" />
            <span className="text-slate-400 text-xs font-mono uppercase tracking-widest">demo access</span>
            <div className="h-px flex-1 bg-sand-border" />
          </div>
          <div className="space-y-2">
            {DEMO_ACCOUNTS.map((a) => (
              <button key={a.email} data-testid={`demo-login-${a.role}`} onClick={() => demoLogin(a.email)}
                className="w-full text-left px-4 py-3 rounded-xl border border-sand-border hover:border-emerald-700/40 hover:bg-emerald-50/50 transition-all group">
                <div className="text-slate-800 text-sm font-medium group-hover:text-emerald-800 transition-colors">{a.label}</div>
                <div className="text-slate-400 text-xs font-mono">{a.email}</div>
              </button>
            ))}
          </div>
        </div>
        <p className="text-center text-slate-400 text-xs mt-8">Protected by Google OAuth • Role-based access control</p>
        <p className="text-center text-slate-400/70 text-[10px] font-mono uppercase tracking-[0.2em] mt-2">Trusted by 85+ Malaysian travel agencies</p>
      </div>
    </div>
  );
}
